import type { FC } from "react";

import { DetailsFieldCell } from "../../../../common-ui/details/DetailsFieldCell";
import {
  MAX_LENGTH_EMPTY_VEHICLE_WEIGHT,
  MAX_LENGTH_HEIGHT,
  MAX_LENGTH_ILU_CODE,
  MAX_LENGTH_LENGTH,
  MAX_LENGTH_VEHICLE_REG,
  MAX_LENGTH_WIDTH,
} from "../../../../common/fieldLengths";
import { transformYesNoOption } from "../../../../common/utils/optionTransformers";
import {
  formatILUCodeString,
  formatRegistrationNumberString,
} from "../../../../common/utils/stringUtil";
import {
  type AllIntermodalVehicleTypesQueryRefProps,
  useAllIntermodalVehicleTypesAsOptions,
} from "../../../../services/intermodal/hooks/useAllIntermodalVehicleTypesAsOptions";
import { DimensionsFieldGrid } from "../../../manage/details/components/edit/BookingDetailsEditContent";
import { yesNoOptions } from "../../../manage/details/components/sections/common";
import { transformIntermodalVehicleType } from "../../utils/detailsUtils";
import { CreateIntermodalValidationWrapper } from "../CreateIntermodalValidationWrapper";
import { CreateIntermodalDropdownField } from "../fields/CreateIntermodalDropdownField";
import { CreateIntermodalNumericTextInputField } from "../fields/CreateIntermodalNumericTextInputField";
import { CreateIntermodalTextInputField } from "../fields/CreateIntermodalTextInputField";

interface Props extends AllIntermodalVehicleTypesQueryRefProps {}

export const CreateIntermodalVehicleSection: FC<Props> = ({
  vehicleTypesQueryRef,
}) => {
  const [vehicleTypes] =
    useAllIntermodalVehicleTypesAsOptions(vehicleTypesQueryRef);

  return (
    <>
      <CreateIntermodalValidationWrapper
        renderField={(isValid) => (
          <DetailsFieldCell
            label={"Vehicle registration"}
            className={"t_intermodal_vehicle_reg_field"}
            renderField={(id) => (
              <CreateIntermodalTextInputField
                id={id}
                trackerCategory={"IntermodalCreate"}
                name={"vehicleReg"}
                isValid={isValid}
                maxLength={MAX_LENGTH_VEHICLE_REG}
                maskInput={formatRegistrationNumberString}
              />
            )}
          />
        )}
        name={"vehicleReg"}
      />
      <CreateIntermodalValidationWrapper
        name={"iluCode"}
        renderField={(isValid) => (
          <DetailsFieldCell
            label={"ILU code"}
            className={"t_intermodal_ilu_code_field"}
            renderField={(id) => (
              <CreateIntermodalTextInputField
                id={id}
                trackerCategory={"IntermodalCreate"}
                name={"iluCode"}
                isValid={isValid}
                maxLength={MAX_LENGTH_ILU_CODE}
                maskInput={formatILUCodeString}
              />
            )}
          />
        )}
      />
      <CreateIntermodalValidationWrapper
        name={"vehicleType"}
        renderField={(isValid) => (
          <DetailsFieldCell
            label={"Vehicle type"}
            renderField={(id) => (
              <CreateIntermodalDropdownField
                id={id}
                trackerCategory={"IntermodalCreate"}
                options={vehicleTypes}
                name={"vehicleType"}
                transformOption={transformIntermodalVehicleType}
                isValid={isValid}
              />
            )}
          />
        )}
      />
      <DetailsFieldCell
        label={"Craneable"}
        renderField={(id) => (
          <CreateIntermodalDropdownField
            id={id}
            trackerCategory={"IntermodalCreate"}
            transformOption={transformYesNoOption}
            name={"craneable"}
            options={yesNoOptions}
          />
        )}
      />
      <CreateIntermodalValidationWrapper
        renderField={(isValid) => (
          <DetailsFieldCell
            label={"Empty vehicle weight (kg)"}
            renderField={(id) => (
              <CreateIntermodalNumericTextInputField
                id={id}
                maxLength={MAX_LENGTH_EMPTY_VEHICLE_WEIGHT}
                trackerCategory={"IntermodalCreate"}
                isValid={isValid}
                name={"emptyWeight"}
                onlyIntegers
              />
            )}
          />
        )}
        name={"emptyWeight"}
      />

      <DimensionsFieldGrid>
        <CreateIntermodalValidationWrapper
          renderField={(isValid) => (
            <DetailsFieldCell
              label={"Length (m)"}
              renderField={(id) => (
                <CreateIntermodalNumericTextInputField
                  id={id}
                  maxLength={MAX_LENGTH_LENGTH}
                  trackerCategory={"IntermodalCreate"}
                  isValid={isValid}
                  name={"length"}
                />
              )}
            />
          )}
          name={"length"}
        />

        <CreateIntermodalValidationWrapper
          renderField={(isValid) => (
            <DetailsFieldCell
              label={"Width (m)"}
              renderField={(id) => (
                <CreateIntermodalNumericTextInputField
                  id={id}
                  maxLength={MAX_LENGTH_WIDTH}
                  trackerCategory={"IntermodalCreate"}
                  isValid={isValid}
                  name={"width"}
                />
              )}
            />
          )}
          name={"width"}
        />

        <CreateIntermodalValidationWrapper
          renderField={(isValid) => (
            <DetailsFieldCell
              label={"Height (m)"}
              renderField={(id) => (
                <CreateIntermodalNumericTextInputField
                  id={id}
                  maxLength={MAX_LENGTH_HEIGHT}
                  trackerCategory={"IntermodalCreate"}
                  isValid={isValid}
                  name={"height"}
                />
              )}
            />
          )}
          name={"height"}
        />
      </DimensionsFieldGrid>
    </>
  );
};
